import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Carousel } from "react-bootstrap";
import defaultBanner from "../../Assets/defaultBanner.png";
import { getImageUrl, trackEvent, useGaTrackingConfig } from "../Utility/Utility";
import MobileSlider from "../Slider/MobileSlider";



const OfferBanner = ({
    placement = "Main",
    promotionPrefix = "top_banner",
    imageKey = "mobileImage",
    requireEnabled = true,
    // useInfiniteMobile = false,
    interval = 6000,
    wrapperClassName = "mainDiv",
    wrapperId,
    sectionClassName = "section-hero pt-4",
    rowClassName = "row hero justify-content-center",
    imageClassName = "d-block w-100",
    dotContainerClassName = "dots-container",
    columnClassName = "col-lg-6 col-12",
    imageStyle,
    desktopSettings = {},
    mobileSettings = {},
    defaultDesktopSliderSettings = {},
    defaultMobileSliderSettings = {},
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const [currentIndex, setCurrentIndex] = useState(0);
    const gaTrackingConfig = useGaTrackingConfig();
    const trackingContext = gaTrackingConfig?.trackingContext || {};
    const isTrackingContextReady =
        !!trackingContext.client_code && !!trackingContext.platform_code && !!trackingContext.affiliation;

    const getOfferMasterData = useSelector(
        (state) => state.offerMasterReducer?.getofferMaster
    );
    const observerRef = useRef(null);
    const firedPromotions = useRef(new Set());
    const currentIndexRef = useRef(0);
    const groupedDataRef = useRef([]);
    const isInViewRef = useRef(false);

    const normalizeSliderSettings = useCallback((settings) => {
        const parsedSlidesToShow = parseFloat(settings?.slidesToShow);
        const parsedSlidesToScroll = parseInt(settings?.slidesToScroll, 10);

        return {
            ...settings,
            slidesToShow: Number.isFinite(parsedSlidesToShow) && parsedSlidesToShow > 0 ? parsedSlidesToShow : 1,
            slidesToScroll: Number.isFinite(parsedSlidesToScroll) && parsedSlidesToScroll > 0 ? parsedSlidesToScroll : 1,
            autoplay: settings?.autoplay !== false,
            autoplaySpeed: Number.isFinite(parseInt(settings?.autoplaySpeed, 10))
                ? parseInt(settings?.autoplaySpeed, 10)
                : 3000,
            infinite: settings?.infinite !== false,
            pauseOnHover: settings?.pauseOnHover !== false,
            speed: Number.isFinite(parseInt(settings?.speed, 10))
                ? parseInt(settings?.speed, 10)
                : 500,
            showDots:
                typeof settings?.showDots === "boolean"
                    ? settings.showDots
                    : !!settings?.dots,
            showControls:
                typeof settings?.showControls === "boolean"
                    ? settings.showControls
                    : !!settings?.arrows,
        };
    }, []);

    const mergedDesktopSettings = useMemo(
        () => normalizeSliderSettings({
            ...defaultDesktopSliderSettings,
            ...desktopSettings,
        }),
        [defaultDesktopSliderSettings, desktopSettings, normalizeSliderSettings]
    );

    const mergedMobileSettings = useMemo(
        () => normalizeSliderSettings({
            ...defaultMobileSliderSettings,
            ...mobileSettings,
        }),
        [defaultMobileSliderSettings, mobileSettings, normalizeSliderSettings]
    );

    const currentSettings = useMemo(
        () => (isMobile ? mergedMobileSettings : mergedDesktopSettings),
        [isMobile, mergedDesktopSettings, mergedMobileSettings]
    );

    const groupedCarouselData = useMemo(() => {
        if (!Array.isArray(getOfferMasterData)) return [];

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const enabledData = getOfferMasterData.filter((item) => {
            const requestedPlacement = (placement || "").toLowerCase();
            const itemPlacement = (item?.placement || "").toLowerCase();
            if (requestedPlacement && itemPlacement && itemPlacement !== requestedPlacement) return false;

            if (requireEnabled) {
                const enabledValue = item?.enabled;
                const isEnabled =
                    enabledValue === true ||
                    enabledValue === 1 ||
                    enabledValue === "1" ||
                    String(enabledValue).toLowerCase() === "true";
                if (enabledValue !== undefined && enabledValue !== null && !isEnabled) return false;
            }

            const hasStartDate = !!item?.startDate;
            const hasEndDate = !!item?.endDate;
            if (!hasStartDate || !hasEndDate) return true;

            const startDate = new Date(item.startDate);
            const endDate = new Date(item.endDate);
            if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return true;

            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            return today >= startDate && today <= endDate;
        });

        const sortedData = [...enabledData].sort(
            (a, b) => parseInt(a.displayOrder, 10) - parseInt(b.displayOrder, 10)
        );

        const positionedData = sortedData.map((item, idx) => ({
            ...item,
            _position: idx + 1,
        }));

        const groupedData = [];
        const length = positionedData.length;
        const slidesToShow = currentSettings.slidesToShow;
        const itemsPerGroup = Math.max(1, Math.ceil(slidesToShow));
        const slidesToScroll = currentSettings.slidesToScroll;

        for (let i = 0; i < length; i += slidesToScroll) {
            const group = positionedData.slice(i, i + itemsPerGroup);
            if (group.length < itemsPerGroup && length > 1 && currentSettings.infinite) {
                let fillerIndex = 0;
                while (group.length < itemsPerGroup && fillerIndex < length) {
                    group.push(positionedData[fillerIndex]);
                    fillerIndex += 1;
                }
            }
            groupedData.push(group);
        }

        return groupedData;
    }, [currentSettings, getOfferMasterData, placement, requireEnabled]);

    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    useEffect(() => {
        groupedDataRef.current = groupedCarouselData;
    }, [groupedCarouselData]);

    useEffect(() => {
        if (!groupedCarouselData.length) return;
        setCurrentIndex(
            (prev) =>
                ((prev % groupedCarouselData.length) + groupedCarouselData.length) %
                groupedCarouselData.length
        );
    }, [groupedCarouselData]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fireViewPromotion = useCallback(
        (group) => {
            if (!isTrackingContextReady || !group?.length) return;

            group.forEach((item) => {
                const itemImage = item?.[imageKey];
                if (!itemImage) return;

                const slot = item?._position || parseInt(item?.displayOrder, 10) || 0;
                const key = `${placement.toLowerCase()}_${slot}_${itemImage}`;
                if (firedPromotions.current.has(key)) return;
                firedPromotions.current.add(key);

                const imageUrl = `${process.env.REACT_APP_CLIENT_URL}${itemImage}`;
                const imageName = itemImage.substring(
                    itemImage.lastIndexOf("/") + 1,
                    itemImage.lastIndexOf(".")
                );

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ ecommerce: null });
                trackEvent("view_promotion", {
                    page_type: "home_page",
                    client_code: trackingContext.client_code,
                    platform_code: trackingContext.platform_code,
                    ecommerce: {
                        creative_name: imageUrl,
                        creative_slot: slot.toString(),
                        promotion_id: imageName,
                        promotion_name: `${promotionPrefix}_${slot}`,
                    },
                });
            });
        },
        [placement, promotionPrefix, imageKey, isTrackingContextReady, trackingContext]
    );

    const carouselCallbackRef = useCallback(
        (node) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            if (!node) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    isInViewRef.current = entry.isIntersecting;
                    if (!entry.isIntersecting) return;
                    const idx = currentIndexRef.current;
                    const group = groupedDataRef.current[idx];
                    if (group) fireViewPromotion(group);
                },
                { threshold: 0.5 }
            );

            observer.observe(node);
            observerRef.current = observer;
        },
        [fireViewPromotion]
    );

    const handleDotClick = (index) => {
        setCurrentIndex(index);
        const group = groupedCarouselData[index];
        if (group) fireViewPromotion(group);
    };

    const handleBannerClick = (offerMasterData, fallbackSlot) => {
        if (!offerMasterData) return;
        const itemImage = offerMasterData?.[imageKey] || "";
        const slot =
            offerMasterData?._position ||
            parseInt(offerMasterData?.displayOrder, 10) ||
            fallbackSlot;

        sessionStorage.setItem("ga4_item_list_name", `${promotionPrefix}_${slot}`);

        trackEvent("select_promotion", {
            page_type: "home_page",
            client_code: trackingContext.client_code,
            platform_code: trackingContext.platform_code,
            ecommerce: {
                creative_name: getImageUrl(itemImage),
                promotion_id: itemImage.substring(
                    itemImage.lastIndexOf("/") + 1,
                    itemImage.lastIndexOf(".")
                ),
                promotion_name: `${promotionPrefix}_${slot}`,
                creative_slot: String(slot),
            },
        });
        window.location.href = offerMasterData?.link;
    };

    if (!groupedCarouselData.length) return null;

    if (isMobile) {
        return (
            <MobileSlider
                groupedCarouselData={groupedCarouselData}
                autoSlideInterval={currentSettings.autoplaySpeed}
                defaultBanner={defaultBanner}
                fireViewPromotion={fireViewPromotion}
                promotionPrefix={promotionPrefix}
                imageKey={imageKey}
                autoplay={currentSettings.autoplay}
                infinite={currentSettings.infinite}
                showDots={currentSettings.showDots}
                slidesToShow={currentSettings.slidesToShow}
                slidesToScroll={currentSettings.slidesToScroll}
                placement={placement}
            />
        );
    }

    return (
        <div className={wrapperClassName} id={wrapperId} ref={carouselCallbackRef}>
            <div className="desktopSliderTitle ">
                <div className="container">
                    {groupedCarouselData?.[0]?.[0]?.title && <h2 className="groupedTitle">{groupedCarouselData?.[0]?.[0]?.title}</h2>}
                    {groupedCarouselData?.[0]?.[0]?.subtitle && <h3 className="groupSubtitle">{groupedCarouselData?.[0]?.[0]?.subtitle}</h3>}
                </div>
            </div>
            <Carousel
                controls={currentSettings.showControls}
                interval={currentSettings.autoplay ? currentSettings.autoplaySpeed : null}
                wrap={currentSettings.infinite}
                pause={currentSettings.pauseOnHover ? "hover" : false}
                activeIndex={currentIndex}
                onSelect={(selectedIndex) => {
                    const length = groupedCarouselData.length;
                    if (!length) return;

                    const normalizedIndex =
                        ((selectedIndex % length) + length) % length;
                    setCurrentIndex(normalizedIndex);

                    if (!isInViewRef.current) return;
                    const group = groupedCarouselData[normalizedIndex];
                    if (group) fireViewPromotion(group);
                }}
            >
                {groupedCarouselData.map((group, index) => (
                    <Carousel.Item key={index}>
                        <section className={sectionClassName}>
                            <div className="container">

                                <div
                                    className={rowClassName}
                                    style={
                                        isMobile
                                            ? { flexWrap: "nowrap", overflow: "hidden" }
                                            : undefined
                                    }
                                >
                                    {group.map((offerMasterData, idx) => {
                                        const itemImage = offerMasterData?.[imageKey];
                                        return (
                                            <div
                                                className={columnClassName}
                                                key={`${index}-${idx}`}
                                                style={{
                                                    flex: `0 0 ${100 / currentSettings.slidesToShow}%`,
                                                    maxWidth: `${100 / currentSettings.slidesToShow}%`,
                                                }}
                                            >
                                                <div className="position-relative">
                                                    <img
                                                        className={imageClassName}
                                                        src={`${process.env.REACT_APP_CLIENT_URL}${itemImage}`}
                                                        alt={itemImage?.substring(
                                                            itemImage?.lastIndexOf("/") + 1,
                                                            itemImage?.lastIndexOf(".")
                                                        )}
                                                        style={imageStyle}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = defaultBanner;
                                                        }}
                                                        onClick={() =>
                                                            handleBannerClick(offerMasterData, index + 1)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>
                    </Carousel.Item>
                ))}

            </Carousel>
            {currentSettings.showDots && groupedCarouselData.length > 1 && (
                <div className={dotContainerClassName}>
                    {groupedCarouselData.map((_, index) => (
                        <span
                            key={index}
                            className={`dot1 ${index === currentIndex ? "active" : ""}`}
                            onClick={() => handleDotClick(index)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default OfferBanner;